import { yupResolver } from '@hookform/resolvers/yup';
import { type FormEvent, forwardRef, memo, useCallback, useId, useImperativeHandle, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { type InferType, object, string } from 'yup';

import { LinkIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { Field, FieldLabel } from '@air/primitive-field';
import { IconButton } from '@air/primitive-icon-button';
import { Input } from '@air/primitive-input';
import { Popover } from '@air/primitive-popover';

export const InsertLinkFormValidationSchema = object({
  text: string().required(),
  url: string().url().required(),
});

export type InsertLinkFormInput = InferType<typeof InsertLinkFormValidationSchema>;

export type InsertLinkButtonHandle = { open: (text: string, url: string) => void };

export const INSERT_LINK_POPOVER_ID = 'insert-link-popover';

export type InsertLinkButtonProps = {
  onInsert: (text: string, url: string) => void;
  onCancel?: () => void;
  getSelectedText?: () => string;
};

export const InsertLinkButton = memo(
  forwardRef<InsertLinkButtonHandle, InsertLinkButtonProps>(({ onInsert, onCancel, getSelectedText }, ref) => {
    const formId = useId();
    const [open, setOpen] = useState(false);

    const {
      formState: { errors, isSubmitting, isValid, touchedFields },
      handleSubmit,
      register,
      reset,
      setValue,
    } = useForm<InsertLinkFormInput>({
      mode: 'all',
      resolver: yupResolver(InsertLinkFormValidationSchema),
    });

    useImperativeHandle(
      ref,
      () => ({
        open: (text, url) => {
          setValue('text', text, { shouldValidate: true });
          setValue('url', url, { shouldValidate: true });
          setOpen(true);
        },
      }),
      [setValue],
    );

    const handleInsert = useCallback(
      ({ text, url }: InsertLinkFormInput) => {
        onInsert(text, url);
        reset();
        setOpen(false);
      },
      [onInsert, reset],
    );

    const handleOpenChange = useCallback(
      (nextOpen: boolean) => {
        if (!nextOpen) {
          reset();
          onCancel?.();
        } else {
          const selectedText = getSelectedText?.();

          if (selectedText) {
            setValue('text', selectedText, { shouldValidate: true });
          }
        }
        setOpen(nextOpen);
      },
      [getSelectedText, onCancel, reset, setValue],
    );

    const handleFormSubmit = useCallback(
      (event: FormEvent<HTMLFormElement>) => {
        event.stopPropagation();
        void handleSubmit(handleInsert)(event);
      },
      [handleInsert, handleSubmit],
    );

    const trigger = useMemo(
      () => (
        <IconButton
          adsIcon
          appearance="ghost"
          color="gray"
          icon={LinkIcon}
          label="Insert link"
          size="small"
          type="button"
        />
      ),
      [],
    );

    return (
      <Popover id={INSERT_LINK_POPOVER_ID} onOpenChange={handleOpenChange} open={open} size="large" trigger={trigger}>
        <form className="flex flex-col gap-3" id={formId} onSubmit={handleFormSubmit}>
          <Field>
            <FieldLabel htmlFor={`${formId}-text`}>Text</FieldLabel>
            <Input
              aria-required="true"
              id={`${formId}-text`}
              placeholder="Link text"
              size="medium"
              {...register('text')}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={`${formId}-url`}>URL</FieldLabel>
            <Input
              aria-describedby={errors.url && touchedFields.url ? `${formId}-url-error` : undefined}
              aria-required="true"
              id={`${formId}-url`}
              placeholder="https://..."
              size="medium"
              type="url"
              {...register('url')}
            />
            {errors.url && touchedFields.url && (
              <p id={`${formId}-url-error`} role="alert" className="ads:text-error text-12 text-red-9">
                Must be a valid URL
              </p>
            )}
          </Field>
          <Button disabled={isSubmitting || !isValid} isLoading={isSubmitting} size="medium" type="submit">
            Insert
          </Button>
        </form>
      </Popover>
    );
  }),
);

InsertLinkButton.displayName = 'InsertLinkButton';
